import { useEffect, useState } from "react";
import { ProcessTable } from "@/components/ProcessTable";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrency, niceName } from "@/lib/format";
import { ArrowDown, ArrowUp } from "lucide-react";

const PERIODS = [["mes", "Mês"], ["trimestre", "Trimestre"], ["ano", "Ano"]];
const num = (v, suf = "") => (v == null ? "—" : `${String(v).replace(".", ",")}${suf}`);
const COLS = [
  ["name", "Consultor", (r) => niceName(r.name)],
  ["listings", "Angariações", (r) => r.listings],
  ["cpcv", "CPCV", (r) => r.cpcv],
  ["deeds", "Escrituras", (r) => r.deeds],
  ["volume", "Volume (€)", (r) => (r.volume ? fmtCurrency(r.volume) : "—")],
  ["avg_price", "Valor médio por casa", (r) => (r.avg_price ? fmtCurrency(r.avg_price) : "—")],
  ["discount", "Desconto médio", (r) => num(r.discount, " %")],
  ["days_listing_cpcv", "Dias angariação → CPCV", (r) => num(r.days_listing_cpcv)],
  ["days_cpcv_deed", "Dias CPCV → escritura", (r) => num(r.days_cpcv_deed)],
  ["pending", "Com pendentes", (r) => r.pending],
  ["goal", "Objetivo anual (€)", (r) => <GoalInput r={r} />],
];

const fmtGoal = (n) => (n === "" || n == null ? "" : `${Number(n).toLocaleString("pt-PT").replace(/\./g, " ")} €`);

function GoalInput({ r }) {
  const [v, setV] = useState(fmtGoal(r.goal));
  const save = async () => {
    const digits = v.replace(/\D/g, "");
    const g = digits === "" ? null : Number(digits);
    setV(fmtGoal(g));
    if (g === (r.goal ?? null)) return;
    try { await api.put(`/users/${r.id}/goal`, { goal: g }); r.goal = g; toast.success("Objetivo guardado"); } catch { toast.error("Não foi possível guardar."); }
  };
  return <input type="text" inputMode="numeric" value={v} placeholder="—" onClick={(e) => e.stopPropagation()} onChange={(e) => setV(e.target.value)} onBlur={save}
    onFocus={() => setV(v.replace(/\D/g, ""))}
    className="w-32 text-right rounded-md border border-slate-200 px-2 py-1 bg-white tabular-nums" data-testid={`team-goal-${r.id}`} />;
}

function Head({ sort, setSort }) {
  return (
    <thead>
      <tr className="text-left text-xs text-slate-500">
        {COLS.map(([k, l]) => (
          <th key={k} className={`py-3 px-3 font-semibold whitespace-nowrap ${k === "name" ? "sticky left-0 bg-white" : "text-right"}`}>
            <button onClick={() => setSort({ k, dir: sort.k === k ? -sort.dir : k === "name" ? 1 : -1 })} className="inline-flex items-center gap-1 hover:text-ec-green" data-testid={`team-sort-${k}`}>
              {l}{sort.k === k && (sort.dir < 0 ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
            </button>
          </th>
        ))}
      </tr>
    </thead>
  );
}

export default function Equipa() {
  const [period, setPeriod] = useState("ano");
  const [query, setQuery] = useState(null);
  const [d, setD] = useState(null);
  const [sort, setSort] = useState({ k: "volume", dir: -1 });
  useEffect(() => { api.get("/agency/team", { params: { period } }).then(({ data }) => setD(data)); }, [period]);
  const rows = [...(d?.rows || [])].sort((a, b) => {
    const x = a[sort.k], y = b[sort.k];
    if (x == null) return 1;
    if (y == null) return -1;
    return (typeof x === "string" ? x.localeCompare(y) : x - y) * sort.dir;
  });
  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl text-ec-green">Equipa</h1>
          <p className="text-slate-500 mt-1">Números calculados a partir dos processos: angariação criada, CPCV carregado, escritura concluída.</p>
        </div>
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-100 w-fit" data-testid="team-period">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`team-period-${k}`}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${period === k ? "bg-ec-green text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-[16px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm" data-testid="team-table">
          <Head sort={sort} setSort={setSort} />
          <tbody>
            {!d ? <tr><td colSpan={COLS.length} className="py-10 text-center text-slate-400">A carregar…</td></tr> : rows.map((r) => (
              <tr key={r.id} onClick={() => setQuery({ title: `${niceName(r.name)} · ${PERIODS.find((p) => p[0] === period)[1]}`, params: { consultant: r.id, period } })} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`team-row-${r.id}`}>
                {COLS.map(([k, , fn]) => (
                  <td key={k} className={`py-3.5 px-3 whitespace-nowrap ${k === "name" ? "sticky left-0 bg-inherit font-medium text-ec-green" : "text-right tabular-nums text-slate-700"} ${k === "pending" && r.pending ? "!text-amber-700 font-semibold" : ""}`}>{fn(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
          {d?.totals && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50/60 font-semibold text-ec-green" data-testid="team-totals">
                {COLS.map(([k, , fn]) => (
                  <td key={k} className={`py-3.5 px-3 whitespace-nowrap ${k === "name" ? "sticky left-0 bg-slate-50" : "text-right tabular-nums"}`} data-testid={`team-total-${k}`}>{k === "name" ? "Total da equipa" : k === "goal" ? "" : fn(d.totals)}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-slate-400">Desconto médio: preço do CMI face ao preço de venda, nas escrituras do período. Totais: soma da equipa; valor médio, desconto e dias calculados sobre todos os negócios. Clique num consultor para ver os processos dele.</p>
      <ProcessTable query={query} onClose={() => setQuery(null)} />
    </div>
  );
}
